// Carousels below the fold ship as empty placeholders and fetch their own markup
// through the Section Rendering API as they approach the viewport. This keeps their
// product cards out of the initial HTML payload and out of the initial DOM.
(() => {
  // Every deferred section emits this script tag, so guard against running twice.
  if (window.__deferredProductListReady) return;
  window.__deferredProductListReady = true;

  const SELECTOR = '[data-deferred-section]';
  // Lead time for the fetch. This needs to exceed a phone screen: at 400px a fast flick
  // arrives at the placeholder before the section request lands, so the reserved box shows
  // empty for a moment and then pops in.
  const ROOT_MARGIN = '1400px 0px';

  // The backstop starts at the first idle moment after the DOM is ready rather than waiting
  // for the window load event, which on a phone waits for every app script - long enough for
  // a shopper to scroll into rows that are still placeholders. The timeout caps the wait when
  // the main thread stays busy, which it does on this page.
  //
  // Starting this early is safe because a fetched row's cards are all lazy: the eager, high
  // priority treatment in blocks/_product-card.liquid is keyed to section.index, which is
  // blank for a Section Rendering API fetch. So this costs markup, not image bandwidth.
  const BACKSTOP_IDLE_TIMEOUT = 600;

  // A section render takes roughly half a second, and a collection page can carry eleven of
  // these, so draining them strictly one at a time would take long enough that a scroller
  // could still outrun it. A small pool keeps the total short without firing every request
  // at once.
  const BACKSTOP_CONCURRENCY = 3;

  const pending = new WeakSet();

  const load = async (placeholder) => {
    if (pending.has(placeholder)) return;
    pending.add(placeholder);

    const url = placeholder.dataset.deferredUrl;
    const section = placeholder.closest('.shopify-section');
    if (!url || !section) return;

    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`Section render failed: ${response.status}`);

      const markup = await response.text();
      const incoming = new DOMParser()
        .parseFromString(markup, 'text/html')
        .querySelector('.shopify-section');

      // Replacing the whole section lets the carousel's custom elements upgrade
      // from scratch with every slide already present.
      if (incoming) section.replaceWith(incoming);
    } catch (error) {
      // Collapse the row rather than breaking the rest of the page. The skeleton has to go
      // with it, otherwise a failed fetch leaves card outlines shimmering indefinitely.
      placeholder.classList.remove('resource-list--deferred');
      placeholder.replaceChildren();
    }
  };

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        observer.unobserve(entry.target);
        load(entry.target);
      }
    },
    { rootMargin: ROOT_MARGIN }
  );

  const observeAll = () => {
    for (const placeholder of document.querySelectorAll(SELECTOR)) {
      observer.observe(placeholder);
    }
  };

  // Even with generous lead time the observer can be outrun, so once the page has settled
  // fill in whatever is still a placeholder.
  //
  // Claims each placeholder before awaiting it rather than re-querying blindly, for two
  // reasons: the workers below must not pick the same one, and a failed load clears the
  // placeholder class but keeps the attribute, which would otherwise loop forever.
  const hydrateRemaining = async () => {
    const claimed = new Set();

    const claimNext = () => {
      const next = [...document.querySelectorAll(SELECTOR)].find((el) => !claimed.has(el));
      if (next) claimed.add(next);
      return next;
    };

    const drain = async () => {
      for (;;) {
        const next = claimNext();
        if (!next) return;

        observer.unobserve(next);
        await load(next);
      }
    };

    await Promise.all(Array.from({ length: BACKSTOP_CONCURRENCY }, drain));
  };

  const scheduleBackstop = () => {
    if (typeof requestIdleCallback === 'function') {
      requestIdleCallback(hydrateRemaining, { timeout: BACKSTOP_IDLE_TIMEOUT });
    } else {
      // Safari has no requestIdleCallback. A plain delay keeps it off the first paint.
      setTimeout(hydrateRemaining, 200);
    }
  };

  const start = () => {
    observeAll();
    scheduleBackstop();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
